import { createContext, useContext, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";

const AuthContext = createContext({ user: null, loading: true, login: async () => {}, register: async () => {}, logout: async () => {} });

// Non-HttpOnly hint cookie set by the backend alongside access/refresh tokens.
// When absent, the visitor is anonymous and we skip the /auth/me probe entirely
// — keeps the browser network log clean on public pages.
function hasSessionHint() {
  if (typeof document === "undefined") return false;
  // Anchor at cookie boundary so a third-party cookie ending in `cg_session=…`
  // can't false-positive (e.g. `xx_cg_session=1`).
  return /(^|;\s*)cg_session=/.test(document.cookie);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchMe = async () => {
    if (!hasSessionHint()) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      // The interceptor returns { user: null, anonymous: true } when the access
      // token is missing or expired and cannot be refreshed. Treat that as logged-out.
      setUser(data?.anonymous ? null : data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchMe(); }, []);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  };

  const register = async (payload) => {
    try {
      const { data } = await api.post("/auth/register", payload);
      setUser(data);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  };

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() { return useContext(AuthContext); }
